/**
 * Colors drawer — mount point for the Design tab's Colors section.
 *
 * Phase B.1 (current): thin adapter that mounts the existing PropertyPanel
 * render/wire methods into the given body element. The actual DOM-building
 * code still lives on PropertyPanel and is reached via the `panel` reference
 * in the context object.
 *
 * Phase B.2: this module will be invoked by `openSectionDrawer({...})` from
 * the design-board overview. The render returns a teardown function so the
 * drawer shell can clean up state-changed subscriptions on close.
 *
 * Over follow-up commits, the body of `panel._renderColorsSubpanel` and
 * `panel._wireColorsSubpanel` migrate into this file, calling `_internals.js`
 * helpers directly. The `panel` reference shrinks as that happens.
 */

import { eventBus } from '../../core/events.js';

/**
 * Mount the Colors content into `bodyEl`. Returns a teardown function that
 * unsubscribes any state-changed listeners attached during the mount.
 *
 * @param {HTMLElement} bodyEl
 * @param {{ panel: import('../property-panel.js').PropertyPanel }} ctx
 */
export function renderColorsContent(bodyEl, { panel }) {
  if (!bodyEl || !panel) return () => {};

  // _renderColorsSubpanel() rebuilds the DOM and calls _wireColorsSubpanel()
  // internally — don't double-wire.
  panel._renderColorsSubpanel(bodyEl);

  // Delegate state-changed updates to the existing _refreshColorsSection,
  // which gates on hasOpenTransaction() and routes through the same
  // revertActivePreviewIfAny() guard the inline-render path used. Avoids
  // detaching live <input>s mid-drag.
  const off = eventBus.on('state-changed', () => panel._refreshColorsSection(bodyEl));
  return () => {
    off?.();
  };
}
